"use client";

import { User, Shield, Settings as SettingsIcon, Lock, Bell, Mail, Trophy, Key, AlertTriangle, Phone, Table2 } from "lucide-react";
import { useSearchParams, useRouter } from "next/navigation";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/app/_module/components/ui/tabs";
import { Alert, AlertDescription, AlertTitle } from "@/app/_module/components/ui/alert";
import { useToast } from "@/app/_module/hooks/use-toast";
import { useUserContext } from "@/app/_module/store/contexts/userContext";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";
import { handleApiCall } from "@/app/_module/utils/apiResponseHandler";
import { useQueryClient } from "@tanstack/react-query";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";

import ProfileInformation from "./ProfileInformation";
import { ForumSettings } from "../ForumSettings";
import ToggleSettings from "./ToggleSettings";
import type { NotificationOption } from "./ToggleSettings";
import {
  LeadExportScopePanel,
  type LeadExportScopeResult,
} from "./LeadExportScopePanel";
import NotificationPreferencesCard from "./NotificationPreferencesCard";
import { BlockedNumbers } from "../BlockedNumbers";
import { MFAManagement } from "./MFAManagement";
import { PasskeyManagement } from "./PasskeyManagement";
import { SecurityActivity } from "./SecurityActivity";
import { EmailPasswordSettings } from "./EmailPasswordSettings";
import { DeleteAccount } from "./DeleteAccount";
import { OAuthAccountConnection } from "./OAuthAccountConnection";
import { getAdminAccess } from "@/app/_module/utils/admin-access";
import { ReplayToursSection } from "@/app/_module/components/onboarding-tour/ReplayToursSection";
import { ApiAccessSettings } from "../ApiAccessSettings";
import { GeneralFeatureToggles } from "./GeneralFeatureToggles";
import { DialerSettings } from "./DialerSettings";
import { TableSettings } from "./TableSettings";
import { accountTypeHasTelephony } from "@/app/_module/components/TelephonyEnabled";

export const AccountSettingsWithTabs = function () {
  const { toast } = useToast();
  const { user, refetchUser } = useUserContext();
  // Dialer preferences only for accounts that can dial (Lite has no phone).
  // The 09-03 Lite change removed the sub-tab for everyone; two deep links
  // (Sentimentals, Dial IQ) still point at prefTab=dialer.
  const hasTelephony = accountTypeHasTelephony(user?.user?.accountType, (user?.user as any)?.trialAccountTypeUsed);
  const searchParams = useSearchParams();
  const router = useRouter();
  const queryClient = useQueryClient();
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  const [agencyLeaderboardEnabled, setAgencyLeaderboardEnabled] = useState(true);
  const [isLoadingAgencyLeaderboard, setIsLoadingAgencyLeaderboard] = useState(false);
  const [allowAgentExport, setAllowAgentExport] = useState(true);
  // Scope picker for turning lead export OFF. The chosen scope is stashed here
  // and read by handleSavePrivacy, which fires on the debounced save that
  // follows the switch flip.
  const [scopeDialogOpen, setScopeDialogOpen] = useState(false);
  const pendingScopeRef = useRef<LeadExportScopeResult | null>(null);
  const scopeResolverRef = useRef<((proceed: boolean) => void) | null>(null);

  const currentAccountTab = searchParams.get("accountTab") || "profile";
  const requestedPrefTab = searchParams.get("prefTab") || "notifications";
  const currentPrefTab = requestedPrefTab === "dialer" && !hasTelephony ? "notifications" : requestedPrefTab;
  const mfaRequired = searchParams.get("mfa") === "required";

  // Check MFA status from user context
  const mfaSettings = user?.user?.mfaSettings;
  // Direct 130 agents: the authenticator app alone completes setup.
  const totpOnly = (user?.user as any)?.mfaSetupReminder?.totpOnly === true;
  const hasBothMfa = mfaSettings?.totpEnabled && (mfaSettings?.smsEnabled || totpOnly);
  const hasTotpEnabled = mfaSettings?.totpEnabled || false;
  const hasSmsEnabled = mfaSettings?.smsEnabled || false;

  // Determine what MFA methods are still needed
  const getMissingMfaMethods = () => {
    if (totpOnly) return "an Authenticator App (TOTP)";
    if (!hasTotpEnabled && !hasSmsEnabled) {
      return "both Authenticator App (TOTP) and SMS verification";
    }
    if (!hasTotpEnabled) {
      return "Authenticator App (TOTP)";
    }
    if (!hasSmsEnabled) {
      return "SMS verification";
    }
    return "";
  };

  // Check if user is agency owner
  const adminAccess = useMemo(() => getAdminAccess(
    user?.user?.userType || '',
    user?.currentAgency?.role
  ), [user?.user?.userType, user?.currentAgency?.role]);
  const isAgencyOwner = !!(adminAccess.isAgencyOwner || adminAccess.isAgencyAdmin);

  const agencyId = user?.currentAgency?.agency?.id;
  const agencyName = user?.currentAgency?.agency?.name || "Agency";

  const fetchAgencyLeaderboardSettings = useCallback(async () => {
    if (!agencyId) return;

    try {
      setIsLoadingAgencyLeaderboard(true);
      const response = await handleApiCall<{ leaderboardEnabled: boolean }>(async () => {
        return await api.get({
          url: getApiUrl(endpoint.agency.getLeaderboardSettings(agencyId)),
          auth: true,
        });
      });

      if (response) {
        setAgencyLeaderboardEnabled(response.leaderboardEnabled ?? true);
      }
    } catch (error: any) {
      // Silently handle 404 errors (endpoint may not exist yet on backend)
      // Only log non-404 errors
      if (error?.response?.status !== 404 && error?.status !== 404) {

      }
      setAgencyLeaderboardEnabled(true);
    } finally {
      setIsLoadingAgencyLeaderboard(false);
    }
  }, [agencyId]);

  const fetchAgencyExportSettings = useCallback(async () => {
    if (!agencyId) return;

    try {
      const response = await handleApiCall<{ allowAgentExport: boolean }>(async () => {
        return await api.get({
          url: getApiUrl(endpoint.agency.getExportSettings(agencyId)),
          auth: true,
        });
      });

      if (response) {
        setAllowAgentExport(response.allowAgentExport ?? true);
      }
    } catch (error: any) {
      // Silently handle 404 errors (endpoint may not exist yet on backend)
      if (error?.response?.status !== 404 && error?.status !== 404) {
        // Optionally log non-404 errors
      }
      setAllowAgentExport(true); // Default to true
    }
  }, [agencyId]);

  // Fetch agency settings (leaderboard and export)
  useEffect(() => {
    if (isAgencyOwner && agencyId) {
      fetchAgencyLeaderboardSettings();
      fetchAgencyExportSettings();
    }
  }, [isAgencyOwner, agencyId, fetchAgencyLeaderboardSettings, fetchAgencyExportSettings]);

  const handleAccountTabChange = (value: string) => {
    // Preserve prefTab when switching to preferences, clear it otherwise
    if (value === "preferences") {
      router.replace(`/settings?tab=account&accountTab=${value}&prefTab=${currentPrefTab}`, { scroll: false });
    } else {
      router.replace(`/settings?tab=account&accountTab=${value}`, { scroll: false });
    }
  };

  const handlePrefTabChange = (value: string) => {
    router.replace(`/settings?tab=account&accountTab=preferences&prefTab=${value}`, { scroll: false });
  };

  // Privacy options configuration - conditionally include agency leaderboard setting
  // Independent agents (no agencyId) default to hidden for privacy/security
  const isIndependentAgent = !agencyId;

  const privacyOptions = useMemo(() => {
    // Typed against the canonical option interface: inference from the first
    // literal narrows the array and rejects optional members (confirmOnDisable)
    // added by later .push calls.
    const baseOptions: NotificationOption[] = [
      {
        id: "profileVisibility",
        label: "Profile Visibility",
        description: "Make your profile visible to other team members",
        defaultValue: true,
      },
      {
        id: "analyticsTracking",
        label: "Personalized Analytics",
        description: "Share individual usage data to personalize your experience. Anonymous platform metrics are always collected.",
        defaultValue: true,
      },
      {
        id: "showInLeaderboard",
        label: "Show My Stats in Leaderboard",
        description: isIndependentAgent
          ? "Display your performance stats in the global leaderboard. For privacy, this is off by default. Enable to compete with other independent agents."
          : "Display your performance stats in the agent leaderboard. When disabled, your stats will be hidden from other agents but you can still view the leaderboard.",
        // Default to ON for all users
        defaultValue: true,
      },
      {
        id: "hideNameOnLeaderboard",
        label: "Hide My Name on Leaderboard",
        description: "When enabled, your name will appear as 'Anonymous' on the leaderboard for privacy while still showing your stats.",
        defaultValue: false,
      },
    ];

    // Add agency leaderboard setting for agency owners
    if (isAgencyOwner && agencyId) {
      baseOptions.push({
        id: "enableLeaderboardForAgents",
        label: "Enable Leaderboard for Agents",
        description: agencyLeaderboardEnabled
          ? "Agents in your agency can view and participate in the leaderboard."
          : "Leaderboard is disabled for all agents in your agency. They cannot view or participate.",
        defaultValue: true,
      });

      // Agent export. Copy deliberately says "everyone below you", NOT "agents":
      // the block now covers sub-agency OWNERS too. The old wording ("restrict
      // exports to owners only") described the previous behaviour, where any
      // role of 'owner' was exempt — which meant it did nothing to a downline,
      // since every sub-agency lead owns their own agency.
      baseOptions.push({
        id: "allowAgentExport",
        label: "Allow Lead Export Below You",
        description: allowAgentExport
          ? "Everyone in your agency and all sub-agencies can export leads to CSV. Turn off to stop exports below you \u2014 you keep yours."
          : "Lead export is off for your agency and every sub-agency under it. You and anyone you've allowed can still export.",
        defaultValue: true,
        // Expands the inline scope picker; resolves false if they back out,
        // which snaps the switch back without saving.
        interceptDisable: () =>
          new Promise<boolean>((resolve) => {
            scopeResolverRef.current = resolve;
            setScopeDialogOpen(true);
          }),
        renderBelow: (checked: boolean) => (
          <LeadExportScopePanel
            // Stays open for as long as export is OFF. Collapsing it after the
            // flip hid the current selection behind a link, so the only obvious
            // way to review or change who was covered was to switch export back
            // ON for the whole downline and off again.
            // Driven by the SWITCH, not by saved server state, so the panel
            // appears the instant it is flipped off and stays put.
            open={scopeDialogOpen || !checked}
            agencyId={agencyId}
            editing={!scopeDialogOpen && !checked}
            onCancel={() => {
              setScopeDialogOpen(false);
              scopeResolverRef.current?.(false);
              scopeResolverRef.current = null;
            }}
            onApply={async (scope) => {
              pendingScopeRef.current = scope;
              const wasFlip = scopeDialogOpen;
              setScopeDialogOpen(false);
              if (wasFlip) {
                // The debounced toggle save will carry the scope.
                scopeResolverRef.current?.(true);
                scopeResolverRef.current = null;
                return;
              }
              // Already locked — no toggle flip will fire, so persist directly.
              try {
                await api.post({
                  url: getApiUrl(`features/${agencyId}/lead-export-lockdown`),
                  // ⚠️ `body`, not `data`. The api helper's Request type has an
                  // index signature, so `data:` type-checks fine and is then
                  // silently dropped — the POST went out empty and the server
                  // fell back to the default 'downline' scope every time.
                  body: { disabled: true, ...scope },
                  auth: true,
                });
                toast({
                  title: "Export restrictions updated",
                  description: "Your new selection has been saved.",
                });
              } catch (e: any) {
                toast({
                  title: "Couldn't save",
                  description: e?.response?.data?.message || "Please try again.",
                  variant: "destructive",
                });
              }
            }}
          />
        ),
      });
    }

    return baseOptions;
  }, [isAgencyOwner, agencyId, agencyLeaderboardEnabled, allowAgentExport, isIndependentAgent, scopeDialogOpen]);

  const handleSavePrivacy = async (settings: Record<string, boolean>, type: string) => {
    try {
      setLeaderboardLoading(true);

      // Map frontend settings to backend expected format
      const privacyData = {
        profileVisibility: settings.profileVisibility ? 'public' : 'private',
        analyticsTracking: settings.analyticsTracking ?? true,
      };

      // Save privacy settings
      const response = await api.put({
        url: getApiUrl('/users/profile/privacy-settings'),
        body: privacyData,
        auth: true,
      });

      // Handle individual leaderboard visibility if changed
      if ('showInLeaderboard' in settings) {
        const visible = settings.showInLeaderboard ?? true;
        await handleApiCall(async () => {
          const response = await api.get({
            url: getApiUrl(endpoint.leaderboard.toggleVisibility(visible)),
          });
          return response;
        });
        // Invalidate leaderboard cache
        await queryClient.invalidateQueries({ queryKey: ['leaderboard-visibility'] });
      }

      // Handle hide name on leaderboard (anonymous mode)
      if ('hideNameOnLeaderboard' in settings) {
        const hide = settings.hideNameOnLeaderboard ?? false;
        await handleApiCall(async () => {
          const response = await api.get({
            url: getApiUrl(endpoint.leaderboard.toggleAnonymous(hide)),
          });
          return response;
        });
        // Invalidate leaderboard cache
        await queryClient.invalidateQueries({ queryKey: ['leaderboard-visibility'] });
        await queryClient.invalidateQueries({ queryKey: ['leaderboard'] });
      }

      // Handle agency leaderboard setting if changed (only for agency owners)
      if ('enableLeaderboardForAgents' in settings && isAgencyOwner && agencyId) {
        const enabled = settings.enableLeaderboardForAgents ?? true;
        await handleApiCall(async () => {
          return await api.put({
            url: getApiUrl(endpoint.agency.updateLeaderboardSettings(agencyId)),
            body: { leaderboardEnabled: enabled },
            auth: true,
          });
        });
        setAgencyLeaderboardEnabled(enabled);
        // Invalidate agency leaderboard settings cache
        await queryClient.invalidateQueries({ queryKey: ['agency-leaderboard-settings', agencyId] });
      }

      // Handle agency export setting if changed (only for agency owners)
      if ('allowAgentExport' in settings && isAgencyOwner && agencyId) {
        const enabled = settings.allowAgentExport ?? true;
        await handleApiCall(async () => {
          return await api.put({
            url: getApiUrl(endpoint.agency.updateExportSettings(agencyId)),
            body: {
              allowAgentExport: enabled,
              // Only meaningful when switching OFF; the API ignores it otherwise.
              ...(enabled ? {} : (pendingScopeRef.current ?? {})),
            },
            auth: true,
          });
        });
        setAllowAgentExport(enabled);
        // Invalidate agency export settings cache
        await queryClient.invalidateQueries({ queryKey: ['agency-export-settings', agencyId] });
      }

      // Update user context with ALL privacy settings including leaderboard
      if (user) {
        refetchUser(prev => ({
          ...prev,
          user: {
            ...prev.user,
            // Update showOnLeaderboard at the top level of user object
            showOnLeaderboard: settings.showInLeaderboard ?? prev.user.showOnLeaderboard ?? true,
            // Update hideNameOnLeaderboard for anonymous mode
            hideNameOnLeaderboard: settings.hideNameOnLeaderboard ?? (prev.user as any).hideNameOnLeaderboard ?? false,
            privacySettings: {
              dataSharing: {
                thirdParty: prev.user.privacySettings?.dataSharing?.thirdParty ?? false,
                analytics: settings.analyticsTracking ?? true,
                marketing: prev.user.privacySettings?.dataSharing?.marketing ?? false,
              },
              accountDeletion: prev.user.privacySettings?.accountDeletion ?? {
                dataExport: true,
                retentionPeriod: 30,
              },
              emailVisibility: prev.user.privacySettings?.emailVisibility ?? 'private',
              phoneVisibility: prev.user.privacySettings?.phoneVisibility ?? 'private',
              showOnlineStatus: prev.user.privacySettings?.showOnlineStatus ?? true,
              profileVisibility: privacyData.profileVisibility as 'public' | 'private',
              showOnLeaderboard: settings.showInLeaderboard ?? prev.user.privacySettings?.showOnLeaderboard ?? true,
              hideNameOnLeaderboard: settings.hideNameOnLeaderboard ?? false,
              allowDirectMessages: prev.user.privacySettings?.allowDirectMessages ?? true,
              notificationPreferences: prev.user.privacySettings?.notificationPreferences ?? {
                email: true,
                sms: false,
                push: true,
              },
            },
          },
        }));
      }

      toast({
        title: "Privacy Settings Updated",
        description: "Your privacy preferences have been saved successfully.",
      });
    } catch (error) {

      toast({
        title: "Error Saving Privacy Settings",
        description: error instanceof Error ? error.message : "Failed to save privacy settings. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLeaderboardLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Tabs value={currentAccountTab} onValueChange={handleAccountTabChange} className="space-y-6">
        <TabsList className="flex w-full overflow-x-auto gap-1 md:grid md:grid-cols-5 scrollbar-hide" data-tour="account-subtabs">
          <TabsTrigger value="profile" className="flex items-center gap-2" data-tour="account-tab-profile">
            <User className="h-4 w-4" />
            <span className="hidden sm:inline">Profile</span>
          </TabsTrigger>
          <TabsTrigger value="security" className="flex items-center gap-2" data-tour="account-tab-security">
            <Shield className="h-4 w-4" />
            <span className="hidden sm:inline">Security</span>
          </TabsTrigger>
          <TabsTrigger value="connections" className="flex items-center gap-2" data-tour="account-tab-connections">
            <Mail className="h-4 w-4" />
            <span className="hidden sm:inline">Connections</span>
          </TabsTrigger>
          <TabsTrigger value="preferences" className="flex items-center gap-2" data-tour="account-tab-preferences">
            <Bell className="h-4 w-4" />
            <span className="hidden sm:inline">Preferences</span>
          </TabsTrigger>
          <TabsTrigger value="api-access" className="flex items-center gap-2" data-tour="account-tab-api-access">
            <Key className="h-4 w-4" />
            <span className="hidden sm:inline">API Access</span>
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile" className="space-y-6" data-tour="account-content-profile">
          <ProfileInformation />
        </TabsContent>


        {/* Security Tab */}
        <TabsContent value="security" className="space-y-6" data-tour="account-content-security">
          {/* MFA Required Alert Banner */}
          {mfaRequired && !hasBothMfa && (
            <Alert variant="destructive" className="bg-red-50 border-red-500">
              <AlertTriangle className="h-5 w-5" />
              <AlertTitle className="text-red-800 font-bold">MFA Setup Required</AlertTitle>
              <AlertDescription className="text-red-700">
                For your security, you must set up {getMissingMfaMethods()} before you can continue using the platform.
                {!totpOnly && (hasTotpEnabled || hasSmsEnabled) && (
                  <span className="block mt-1 font-medium">
                    You're almost there! {hasTotpEnabled ? "TOTP is already enabled ✓" : "SMS is already enabled ✓"}
                  </span>
                )}
              </AlertDescription>
            </Alert>
          )}
          <MFAManagement />
          <PasskeyManagement />
          <SecurityActivity />
          <EmailPasswordSettings />
          <DeleteAccount />
        </TabsContent>

        {/* Connections Tab */}
        <TabsContent value="connections" className="space-y-6" data-tour="account-content-connections">
          <OAuthAccountConnection />
        </TabsContent>

        {/* Preferences Tab — with nested sub-tabs for each category */}
        <TabsContent value="preferences" className="space-y-6" data-tour="account-content-preferences">
          <Tabs value={currentPrefTab} onValueChange={handlePrefTabChange} className="w-full">
            <TabsList className={`flex w-full min-w-0 overflow-x-auto gap-1 md:grid ${hasTelephony ? "md:grid-cols-5" : "md:grid-cols-4"} md:justify-center mb-6 scrollbar-hide`} data-tour="preferences-subtabs">
              <TabsTrigger value="notifications" className="flex items-center gap-2" data-tour="pref-tab-notifications">
                <Bell className="h-4 w-4" />
                <span className="hidden sm:inline">Notifications</span>
              </TabsTrigger>
              <TabsTrigger value="privacy" className="flex items-center gap-2" data-tour="pref-tab-privacy">
                <Shield className="h-4 w-4" />
                <span className="hidden sm:inline">Privacy</span>
              </TabsTrigger>
              <TabsTrigger value="general" className="flex items-center gap-2" data-tour="pref-tab-general">
                <SettingsIcon className="h-4 w-4" />
                <span className="hidden sm:inline">General</span>
              </TabsTrigger>
              {hasTelephony && (
                <TabsTrigger value="dialer" className="flex items-center gap-2" data-tour="pref-tab-dialer">
                  <Phone className="h-4 w-4" />
                  <span className="hidden sm:inline">Dialer</span>
                </TabsTrigger>
              )}
              <TabsTrigger value="tables" className="flex items-center gap-2" data-tour="pref-tab-tables">
                <Table2 className="h-4 w-4" />
                <span className="hidden sm:inline">Tables</span>
              </TabsTrigger>
            </TabsList>

            {/* Notifications */}
            <TabsContent value="notifications" className="space-y-6">
              <NotificationPreferencesCard />
            </TabsContent>

            {/* Privacy */}
            <TabsContent value="privacy" className="space-y-6">
              <ToggleSettings
                description="Manage your privacy preferences and data sharing"
                icon={<Shield className="h-5 w-5" />}
                onSave={handleSavePrivacy}
                options={privacyOptions}
                saveButtonText="Save Privacy Settings"
                title="Privacy & Security"
                type="privacy"
                agencyLeaderboardEnabled={agencyLeaderboardEnabled}
                agencyName={agencyName}
                isAgencyOwner={isAgencyOwner}
                allowAgentExport={allowAgentExport}
              />
              <BlockedNumbers />
            </TabsContent>

            {/* General */}
            <TabsContent value="general" className="space-y-6">
              <GeneralFeatureToggles />
              <ForumSettings />
              <ReplayToursSection />
            </TabsContent>

            {/* Dialer */}
            {hasTelephony && (
              <TabsContent value="dialer" className="space-y-6">
                <DialerSettings />
              </TabsContent>
            )}

            {/* Tables */}
            <TabsContent value="tables" className="space-y-6">
              <TableSettings />
            </TabsContent>
          </Tabs>
        </TabsContent>

        {/* API Access Tab - Available to all users */}
        <TabsContent value="api-access" className="space-y-6" data-tour="account-content-api-access">
          <ApiAccessSettings />
        </TabsContent>
      </Tabs>
    </div>
  );
};
